/*
 * Whether a failed request is worth repeating. A 401 is not: the token
 * is dead, and every repetition asks the same dead token again — three
 * of them with backoff only keep the loading skeleton on screen for
 * seconds while the reader cannot tell a slow page from a broken one.
 * Failing at once lets the page say what happened, while the auth
 * provider renews the session or sends the reader to the login.
 */
export const RETRY_LIMIT = 3;

import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';

export const retryUnlessUnauthorized = (
  failureCount: number,
  error: unknown
): boolean => responseOf(error)?.status !== 401 && failureCount < RETRY_LIMIT;

/*
 * Maps a failed API call (an axios error) to a friendly, translated
 * message that the frontend derives from the HTTP status alone. The
 * backend's own message text is deliberately not used here: its texts
 * are only partly translated and sometimes raw exception or database
 * strings (see requirements/backend_requirements.md, BE-9), so it
 * belongs into the collapsible technical details of the ErrorDialog,
 * not into the headline. `fallbackKey` lets the caller pick a
 * context-appropriate default (e.g. loading vs. saving).
 */
export function apiErrorMessage(
  error: unknown,
  t: TFunction,
  { fallbackKey = 'error.generic' }: { fallbackKey?: string } = {}
): string {
  const response = responseOf(error);
  /* no response object means the request never completed: network
     down, timeout, CORS, server unreachable */
  if (response == null) {
    return t('error.network');
  }

  switch (response.status) {
    case 401:
    case 403:
      return t('error.forbidden');
    case 404:
      return t('error.notFound');
    case 409:
      return isForeignKeyViolation(response.data)
        ? t('error.stillInUse')
        : t('error.conflict');
    default:
      return t(fallbackKey);
  }
}

/*
 * A refused delete reaches the client as a 409 carrying the database's
 * own words (BE-2). Those words distinguish it from a genuine edit
 * conflict: a foreign key violation means the record is still
 * referenced by other data, not that it changed in the meantime.
 */
function isForeignKeyViolation(data: unknown): boolean {
  const raw = messageOf(data) ?? '';
  return raw.toLowerCase().includes('violates foreign key constraint');
}

/*
 * The technical side of the same error, for the collapsible details of
 * the ErrorDialog: the HTTP status, the backend's own message, the
 * error id the backend will send once BE-10 is built, and the
 * transport-level message (e.g. "Network Error") for calls that never
 * got a response.
 */
export function apiErrorDetails(error: unknown): {
  status: number | null;
  backendMessage: string | null;
  errorId: string | null;
  transportMessage: string | null;
} {
  const response = responseOf(error);
  const data = response?.data;
  const errorId = fieldOf(data, 'errorId');
  const message = fieldOf(error, 'message');
  return {
    status: response?.status ?? null,
    backendMessage: serverErrorMessage(data),
    errorId: typeof errorId === 'string' ? errorId : null,
    transportMessage: typeof message === 'string' ? message : null,
  };
}

/*
 * The backend (CustomErrorResponse) wraps its error text in a `message`
 * field, prefixed with "ERROR: [<status>] " and — when a translation is
 * missing — decorated with 【⍰ … ⍰】. Unwrap both and return the plain
 * message; the status has its own line in the details. Returns null for
 * empty or non-human bodies (e.g. a plain string HTML error page).
 */
function serverErrorMessage(data: unknown): string | null {
  const raw = messageOf(data);
  if (!raw) {
    return null;
  }

  const cleaned = raw
    .replace(/^ERROR:\s*\[\d+\]\s*/i, '')
    .replace(/^【⍰\s*/, '')
    .replace(/\s*⍰】$/, '')
    .trim();

  /* ignore empty payloads or an HTML error page served as a string */
  if (!cleaned || cleaned.startsWith('<')) {
    return null;
  }
  return cleaned;
}

/* callers hand in whatever a query or mutation rejected with, and tests
   stand in plain objects for axios errors — so the shape is probed, not
   narrowed with isAxiosError alone */
function responseOf(
  error: unknown
): { status?: number; data?: unknown } | undefined {
  if (isAxiosError(error)) return error.response;
  const response = fieldOf(error, 'response');
  return typeof response === 'object' && response !== null
    ? response
    : undefined;
}

function fieldOf(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

/* the backend's text, whether it came as the whole body or wrapped in
   a `message` field */
function messageOf(data: unknown): string | null {
  if (typeof data === 'string') return data;
  const message = fieldOf(data, 'message');
  return typeof message === 'string' ? message : null;
}
